import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../auth/AuthProvider';
import Seo from '../../components/Seo';

const PAGE_SIZE = 30;
const RECENT_PROOF_HOURS = 24;

const People = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [isReportOpen, setIsReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('spam');
  const [reportDetails, setReportDetails] = useState('');
  const [isSubmittingReport, setIsSubmittingReport] = useState(false);

  const getLevelInfo = (scoreValue) => {
    const thresholds = [0, 100, 300, 600, 1000];
    let level = 1;
    let current = 0;
    let next = 100;

    for (let i = thresholds.length - 1; i >= 0; i -= 1) {
      if (scoreValue >= thresholds[i]) {
        level = i + 1;
        current = thresholds[i];
        next = thresholds[i + 1] ?? (thresholds[i] + 500);
        break;
      }
    }

    const progress = next > current
      ? Math.min(100, ((scoreValue - current) / (next - current)) * 100)
      : 100;

    return { level, current, next, progress };
  };

  const offset = useMemo(() => (page - 1) * PAGE_SIZE, [page]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .rpc('ch_list_public_users_with_latest_proof', { p_limit: PAGE_SIZE + 1, p_offset: offset });

      if (error) {
        setUsers([]);
        setHasNextPage(false);
        return;
      }

      const rows = data || [];
      setHasNextPage(rows.length > PAGE_SIZE);

      const pageRows = rows.slice(0, PAGE_SIZE);
      const baseUsers = pageRows.map((r) => ({
        userId: r.user_id,
        username: r.username,
        avatarUrl: r.avatar_url || '',
        latestProofAt: r.latest_proof_at,
        latestProofId: r.latest_proof_id,
      }));

      const userIds = baseUsers.map((u) => u.userId).filter(Boolean);

      const [profilesRes, projectsRes, challengesRes, proofsRes] = userIds.length > 0
        ? await Promise.all([
          supabase
            .from('ch_profiles')
            .select('id, score')
            .in('id', userIds),
          supabase
            .from('ch_projects')
            .select('id, owner_id')
            .in('owner_id', userIds)
            .eq('is_public', true),
          supabase
            .from('ch_challenges')
            .select('id, owner_id, goal_type, target_count, total_goals')
            .in('owner_id', userIds)
            .eq('is_public', true),
          supabase
            .from('ch_proofs')
            .select('id, user_id, challenge_id, submitted_at')
            .in('user_id', userIds)
            .eq('is_public', true)
            .order('submitted_at', { ascending: false })
            .limit(5000),
        ])
        : [{ data: [] }, { data: [] }, { data: [] }, { data: [] }];

      const scoreByUser = new Map((profilesRes?.data || []).map((p) => [p.id, Number(p.score || 0)]));

      const projectsByUser = new Map();
      (projectsRes?.data || []).forEach((p) => {
        if (!p?.owner_id) return;
        projectsByUser.set(p.owner_id, (projectsByUser.get(p.owner_id) || 0) + 1);
      });

      const proofsByUser = new Map();
      const attendanceDaySetByUser = new Map();
      const proofCountByChallengeId = new Map();

      (proofsRes?.data || []).forEach((pr) => {
        if (pr?.user_id) {
          proofsByUser.set(pr.user_id, (proofsByUser.get(pr.user_id) || 0) + 1);
          const dayKey = pr.submitted_at ? String(pr.submitted_at).slice(0, 10) : '';
          if (dayKey) {
            const set = attendanceDaySetByUser.get(pr.user_id) || new Set();
            set.add(dayKey);
            attendanceDaySetByUser.set(pr.user_id, set);
          }
        }
        if (pr?.challenge_id) {
          proofCountByChallengeId.set(pr.challenge_id, (proofCountByChallengeId.get(pr.challenge_id) || 0) + 1);
        }
      });

      const completedChallengesByUser = new Map();
      (challengesRes?.data || []).forEach((ch) => {
        const ownerId = ch?.owner_id;
        if (!ownerId || !ch?.id) return;

        const goalType = ch.goal_type === 'thirty-day' ? 'thirty_day' : ch.goal_type;
        const total = goalType === 'thirty_day'
          ? 30
          : Number(ch.total_goals ?? ch.target_count ?? 0);
        const done = proofCountByChallengeId.get(ch.id) || 0;
        if (total > 0 && done >= total) {
          completedChallengesByUser.set(ownerId, (completedChallengesByUser.get(ownerId) || 0) + 1);
        }
      });

      const mapped = baseUsers.map((u) => {
        const score = scoreByUser.get(u.userId) || 0;
        const levelInfo = getLevelInfo(score);
        const attendanceDays = attendanceDaySetByUser.get(u.userId)?.size || 0;
        return {
          ...u,
          level: levelInfo.level,
          levelProgress: levelInfo.progress,
          attendanceDays,
          totalProjects: projectsByUser.get(u.userId) || 0,
          completedChallenges: completedChallengesByUser.get(u.userId) || 0,
          submittedProofs: proofsByUser.get(u.userId) || 0,
        };
      });

      setUsers(mapped);
    } finally {
      setIsLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    load();
  }, [load]);

  const reportReasonOptions = useMemo(
    () => [
      { value: 'spam', label: '스팸/광고' },
      { value: 'abuse', label: '욕설/혐오/괴롭힘' },
      { value: 'illegal', label: '불법/유해 정보' },
      { value: 'copyright', label: '저작권/도용' },
      { value: 'other', label: '기타' },
    ],
    []
  );

  const openReport = (e, u) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (!u?.latestProofId) {
      alert('신고할 증명 정보가 없습니다.');
      return;
    }

    if (!user?.id) {
      alert('신고하려면 로그인이 필요합니다.');
      navigate('/login-registration');
      return;
    }

    setReportTarget({
      proofId: u.latestProofId,
      reportedUserId: u.userId,
      reportedUsername: u.username,
    });
    setReportReason('spam');
    setReportDetails('');
    setIsReportOpen(true);
  };

  const submitReport = async () => {
    if (!user?.id) return;
    if (!reportTarget?.proofId) return;
    if (!reportReason) {
      alert('신고 사유를 선택해주세요.');
      return;
    }

    setIsSubmittingReport(true);
    try {
      const { error } = await supabase
        .from('ch_reports')
        .insert({
          reporter_id: user.id,
          target_type: 'proof',
          proof_id: reportTarget.proofId,
          reason: reportReason,
          details: reportDetails?.trim() || null,
        });

      if (error) {
        if (error.code === '23505') {
          alert('이미 신고한 콘텐츠입니다.');
        } else {
          alert(error.message || '신고 제출에 실패했습니다.');
        }
        return;
      }

      alert('신고가 접수되었습니다. 감사합니다.');
      setIsReportOpen(false);
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="사람들 - 꿈을 향한 도전 커뮤니티 | 꿈현.Labs" canonicalPath="/people" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-2xl mx-auto space-y-6 md:space-y-8">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2">
                사람들
              </h1>
              <p className="text-sm md:text-base text-muted-foreground">
                최근 공개 증명을 등록한 유저들을 둘러보세요.
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className="bg-card rounded-lg shadow-gaming-md p-6">
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-card rounded-lg shadow-gaming-md p-6">
              <p className="text-sm text-muted-foreground caption">표시할 유저가 없습니다.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {users.map((u) => (
                <div
                  key={u.userId}
                  className="relative"
                  style={{ contentVisibility: 'auto', containIntrinsicSize: '1px 260px', contain: 'layout paint' }}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/people/user?id=${u.userId}`)}
                    className="w-full bg-card rounded-2xl border border-border overflow-hidden shadow-gaming-md transition-gaming hover:shadow-gaming-lg active:translate-y-px active:shadow-gaming-md"
                    aria-label={u.username ? `${u.username} 프로필 보기` : '유저 프로필 보기'}
                  >
                    <div className="relative">
                      <div className="aspect-[3/2] bg-gradient-to-br from-primary/20 via-muted/10 to-accent/10">
                        {u.avatarUrl ? (
                          <img
                            src={u.avatarUrl}
                            alt={u.username || 'avatar'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Icon name="User" size={56} className="text-muted-foreground" />
                          </div>
                        )}
                      </div>

                      <div className="absolute top-3 left-3 flex items-center gap-2">
                        <div className="px-2.5 py-1 rounded-full bg-background/80 border border-border text-xs font-bold text-foreground backdrop-blur">
                          Lv.{Number(u.level || 1)}
                        </div>
                        {(() => {
                          const ts = u?.latestProofAt ? new Date(u.latestProofAt).getTime() : 0;
                          const isRecent = ts > 0 && Date.now() - ts <= RECENT_PROOF_HOURS * 60 * 60 * 1000;
                          return isRecent ? (
                            <div className="px-2.5 py-1 rounded-full bg-background/80 border border-border text-xs font-semibold text-success backdrop-blur">
                              HOT
                            </div>
                          ) : null;
                        })()}
                      </div>

                      <div className="absolute bottom-3 left-3 right-3">
                        <div className="text-lg font-heading font-bold text-foreground truncate drop-shadow">
                          {u.username || '익명'}
                        </div>
                        <div className="mt-1 w-full h-2 bg-background/60 rounded-full overflow-hidden border border-border">
                          <div
                            className="h-full bg-primary"
                            style={{ width: `${Math.min(100, Number(u.levelProgress || 0))}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-xl border border-border bg-muted/30 p-3 text-left">
                          <div className="text-[11px] text-muted-foreground caption">출석일수</div>
                          <div className="text-base font-bold text-foreground data-text">{Number(u.attendanceDays || 0).toLocaleString('ko-KR')}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-muted/30 p-3 text-left">
                          <div className="text-[11px] text-muted-foreground caption">등록 목표</div>
                          <div className="text-base font-bold text-foreground data-text">{Number(u.totalProjects || 0).toLocaleString('ko-KR')}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-muted/30 p-3 text-left">
                          <div className="text-[11px] text-muted-foreground caption">완료 챌린지</div>
                          <div className="text-base font-bold text-foreground data-text">{Number(u.completedChallenges || 0).toLocaleString('ko-KR')}</div>
                        </div>
                        <div className="rounded-xl border border-border bg-muted/30 p-3 text-left">
                          <div className="text-[11px] text-muted-foreground caption">제출한 증명</div>
                          <div className="text-base font-bold text-foreground data-text">{Number(u.submittedProofs || 0).toLocaleString('ko-KR')}</div>
                        </div>
                      </div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={(e) => openReport(e, u)}
                    disabled={!u?.latestProofId}
                    className={`absolute top-3 right-3 z-10 p-2 rounded-md bg-background/90 backdrop-blur-sm border border-border transition-gaming ${
                      u?.latestProofId
                        ? 'hover:bg-background'
                        : 'opacity-50 cursor-not-allowed'
                    }`}
                    aria-label="신고하기"
                  >
                    <Icon name="Flag" size={16} className="text-destructive" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || isLoading}
              className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
            >
              이전
            </button>

            <div className="text-sm text-muted-foreground caption">
              페이지 {page}
            </div>

            <button
              type="button"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNextPage || isLoading}
              className="px-4 py-2 rounded-md bg-muted/40 text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:bg-muted transition-gaming"
            >
              다음
            </button>
          </div>

          {isReportOpen && reportTarget && (
            <div
              className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
              onClick={() => !isSubmittingReport && setIsReportOpen(false)}
            >
              <div
                className="w-full max-w-lg bg-card rounded-lg border border-border shadow-gaming-lg p-4 md:p-6"
                onClick={(e) => e?.stopPropagation()}
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="text-lg md:text-xl font-heading font-semibold text-foreground">신고하기</div>
                    <div className="text-xs md:text-sm text-muted-foreground caption mt-1">
                      {reportTarget.reportedUsername}님의 공개 증명을 신고합니다.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => !isSubmittingReport && setIsReportOpen(false)}
                    className="p-2 rounded-md hover:bg-muted transition-gaming text-muted-foreground"
                  >
                    <Icon name="X" size={18} />
                  </button>
                </div>

                <div className="space-y-4">
                  <Select
                    options={reportReasonOptions}
                    value={reportReason}
                    onChange={setReportReason}
                    placeholder="신고 사유 선택"
                  />

                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">상세 내용(선택)</label>
                    <textarea
                      value={reportDetails}
                      onChange={(e) => setReportDetails(e?.target?.value)}
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-background border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-gaming resize-none"
                      placeholder="추가로 전달할 내용이 있으면 적어주세요."
                      disabled={isSubmittingReport}
                    />
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <Button variant="outline" onClick={() => setIsReportOpen(false)} disabled={isSubmittingReport}>
                      취소
                    </Button>
                    <Button
                      variant="destructive"
                      iconName="Flag"
                      iconPosition="left"
                      onClick={submitReport}
                      loading={isSubmittingReport}
                    >
                      신고 제출
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default People;
